import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  OperationType,
  TAppsyncChatData,
} from '@/console/features/factory-operator/hooks/use-chat/use-chat.types';
import { useAppSyncEvents } from '@/shared/hooks/use-appsync-events/use-app-sync-events';
import { useLoggedInUser } from '@/shared/hooks/use-logged-in-user/use-logged-in-user';
import { useReadTenantQuery } from '@/shared/redux/api/ident/ident-api';
import { sdaiApi } from '@/shared/redux/api/sdai/sdai-api';
import { showToastError } from '@/shared/utils/toast';

export const useChat = (chatId: string) => {
  const { t } = useTranslation();
  const [operatorThinking, setOperatorThinking] = useState<boolean>(false);
  const { data: tenantData } = useReadTenantQuery();
  const { loggedInUser } = useLoggedInUser();
  const dispatch = useDispatch();
  const webSocketChannel = `default/${tenantData?.readTenant.tenantId}/sdai/${loggedInUser?.userId}`;
  const [message, setMessage] = useState('');
  const chatIdRef = useRef(chatId);
  chatIdRef.current = chatId;

  const onMessageReceived = (data: TAppsyncChatData) => {
    if (chatIdRef.current !== data.event.chat_id) {
      return;
    }

    if (data.event.operation_type === OperationType.THINKING) {
      setOperatorThinking(true);
      return;
    }

    if (data.event.operation_type === OperationType.ERROR) {
      showToastError(t('factory_operator:user_response_error'));
      setMessage(data.event.chat_object.message);
    }

    setOperatorThinking(false);

    dispatch(
      sdaiApi.util.invalidateTags([{ type: 'Chat', id: data.event.chat_id }]),
    );
  };

  useAppSyncEvents({
    connectOnInit: true,
    onMessageReceived,
    webSocketChannel,
  });

  return { operatorThinking, setOperatorThinking, message, setMessage };
};
